import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LogIn, Shield, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

interface AdminAuthProps {
  children: React.ReactNode;
}

const AdminAuth: React.FC<AdminAuthProps> = ({ children }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  if (!user?.isAdmin) {
    return <UnauthorizedScreen />;
  }

  return <>{children}</>;
};

const LoadingScreen: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-twitch-600 mx-auto mb-4"></div>
      <h2 className="text-lg font-medium text-gray-900">Loading...</h2>
      <p className="text-gray-500">Checking authentication status</p>
    </div>
  </div>
);

const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, getAuthUrl } = useAuth();

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      toast.error(`Authentication failed: ${error}`);
      navigate('/login', { replace: true });
      return;
    }

    if (code && state) {
      handleCallback(code, state);
    }
  }, [searchParams, navigate]);

  const handleCallback = async (code: string, state: string) => {
    try {
      await login(code, state);
      toast.success('Successfully logged in!');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login callback failed:', error);
      toast.error('Login failed. Please try again.');
      navigate('/login', { replace: true });
    }
  };

  const handleTwitchLogin = async () => {
    try {
      const authUrl = await getAuthUrl();
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to get auth URL:', error);
      toast.error('Failed to initiate login. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Shield className="h-12 w-12 text-twitch-600" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Admin Portal
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Sign in with your Twitch account to access the admin panel
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card px-4 py-8 shadow sm:px-10">
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-amber-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">
                    Admin Access Required
                  </h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      This portal is restricted to authorized administrators only.
                      You must have admin privileges to access this system.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleTwitchLogin}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-twitch-600 hover:bg-twitch-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-twitch-500 transition-colors"
            >
              <LogIn className="h-5 w-5 mr-2" />
              Sign in with Twitch
            </button>

            <div className="text-xs text-gray-500 text-center">
              By signing in, you agree to our terms of service and privacy policy.
              This system logs all administrative actions for security purposes.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UnauthorizedScreen: React.FC = () => {
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <AlertCircle className="h-12 w-12 text-red-500" />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Access Denied
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          You don't have administrator privileges
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="card px-4 py-8 shadow sm:px-10">
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Insufficient Permissions
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      Your account does not have the required administrator permissions
                      to access this system. Please contact a system administrator
                      if you believe this is an error.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;